import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { twMerge } from 'tailwind-merge';

import { Select } from '@/_components';
import { SelectorWithAvatarContainer } from '@/_containers';
import { type SystemUserQueryLinkerSchema, useGetUsersMutation } from '@/_pages/system-users/shared';
import { useGetTeamMembersMutation } from '@/_pages/team-members/shared';
import type { TeamMemberQueryLinkerSchema } from '@/_schemas/team-members/team-members-query-linker-schema';
import { nameResolver, useBoolean, useDebounce } from '@/_shared';

import type { PickerComboboxProps, PickerGroupedOption, PickerOptionType } from './helper';
import { getLogo, isTeamMember, isUser } from './helper';

import { usePickTicketsFormContext } from '../../form-context';
import type { PickerTypeSchema } from '../../schemas/linked-schemas';

export function PickerCombobox({ disabled = false, className, autoFocus = false }: PickerComboboxProps) {
  const form = usePickTicketsFormContext();
  const [picker] = form.watch(['picker']);

  const [isFocused, focus] = useBoolean();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 500);
  const fetchDefaultOptionsRef = useRef(true);

  const {
    mutateAsync: getUsersOptions,
    data: usersData,
    isPending: isLoadingUsers,
  } = useGetUsersMutation({
    onSuccess: () => {
      focus.setFalse();
    },
  });

  const {
    mutateAsync: getTeamMembersOptions,
    data: teamMembersData,
    isPending: isLoadingTeamMembers,
  } = useGetTeamMembersMutation();

  const formatGroupLabel = useCallback((data: PickerGroupedOption) => <div>{data.label}</div>, []);
  const getOptionLabel = useCallback(
    (option: PickerOptionType) => nameResolver(option?.first_name, option?.last_name),
    []
  );
  const getOptionValue = useCallback((option: PickerOptionType) => option?.id.toString(), []);

  const fetchOptions = useCallback(
    (search = '', per_page = 100) => {
      if ((!isFocused || disabled) && !search) {
        return;
      }
      getUsersOptions({ search, per_page });
      getTeamMembersOptions({ search, per_page });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled, isFocused]
  );

  const updateValue = useCallback(
    (val: SystemUserQueryLinkerSchema | TeamMemberQueryLinkerSchema | null, type: PickerTypeSchema | null) => {
      form.setValue('picker', val, { shouldDirty: true });
      form.setValue('picker_id', val ? val.id : null, { shouldDirty: true });
      form.setValue('picker_type', type, { shouldDirty: true });
    },
    [form]
  );

  const handleChange = useCallback(
    (v: PickerOptionType | null) => {
      if (!v) {
        updateValue(null, null);
      } else if (isTeamMember(v)) {
        updateValue(v, 'team-member');
      } else if (isUser(v)) {
        updateValue(v, 'user');
      }
    },
    [updateValue]
  );

  useEffect(() => {
    if (fetchDefaultOptionsRef.current) {
      fetchOptions();
      fetchDefaultOptionsRef.current = false;
    }
  }, [fetchOptions]);

  useEffect(() => {
    if (!fetchDefaultOptionsRef.current) {
      fetchOptions(debouncedQuery);
    }
  }, [fetchOptions, debouncedQuery]);

  const groupedOptions = useMemo(
    (): PickerGroupedOption[] => [
      {
        label: 'Users',
        options: usersData || [],
      },
      {
        label: 'Team Members',
        options: teamMembersData || [],
      },
    ],
    [usersData, teamMembersData]
  );

  return (
    <SelectorWithAvatarContainer
      disabled={disabled}
      src={getLogo(picker)}
      label={nameResolver(picker?.first_name, picker?.last_name, 'Picker')}
      name={picker?.first_name ?? ''}
      className={twMerge(className, 'lg:flex-row')}
    >
      <Select<PickerOptionType, false, PickerGroupedOption>
        label="Picker"
        value={picker}
        options={groupedOptions}
        formatGroupLabel={formatGroupLabel}
        onInputChange={setQuery}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        onChange={handleChange}
        onFocus={focus.setTrue}
        onBlur={focus.setFalse}
        isLoading={isLoadingUsers || isLoadingTeamMembers}
        disabled={disabled}
        autoFocus={autoFocus}
      />
    </SelectorWithAvatarContainer>
  );
}
